import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { 
  ClockIcon, 
  ExclamationTriangleIcon,
  ArrowRightIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import { RootState, AppDispatch } from '../../store'
import { getSessionStatus, submitAssessment } from '../../store/slices/testSlice'
import toast from 'react-hot-toast'
import { driveAPI } from '../../services/api'
import type { DriveAssessmentConfigs } from '../../types'
import AutomaticTestInstructions from '../../components/AutomaticTestInstructions'
import { testGuidelines } from '../../utils/testInstructions'

const TestInstructionsPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<AppDispatch>()
  const { currentSession, isLoading, error } = useSelector((state: RootState) => state.test)
  const { user } = useSelector((state: RootState) => state.auth)
  const [driveConfigs, setDriveConfigs] = useState<DriveAssessmentConfigs>({})
  const driveId = String(currentSession?.drive_id ?? String(currentSession?.assessment_id ?? '').match(/^(\d+)_/)?.[1] ?? sessionId?.match(/^session-assessment_(\d+)_/)?.[1] ?? '')
  const sectionType = currentSession?.assessment_type || String(currentSession?.assessment_id ?? '').replace(/^\d+_/, '')
  const sectionConfig = driveConfigs[sectionType]
  const guidelines = testGuidelines(currentSession?.proctoring_enabled !== false)

  useEffect(() => {
    let cancelled = false
    setDriveConfigs({})
    if (driveId && /^\d+$/.test(driveId) && user?.email) {
      driveAPI.getMyRegistrations(user.email).then(({ data }) => {
        const drive = data.find((entry: { id: number | string }) => String(entry.id) === driveId)
        if (!drive) throw new Error('Registered test not found')
        if (!cancelled) setDriveConfigs(drive.assessment_configs ?? {})
      }).catch(() => {
        if (!cancelled) toast.error('Could not load the test configuration. Refresh to see the section details.')
      })
    }
    return () => { cancelled = true }
  }, [driveId, user?.email])

  // State for tracking checked guidelines
  const [checkedGuidelines, setCheckedGuidelines] = useState<boolean[]>(new Array(5).fill(false))
  
  // Assessment sequence navigation prevention
  const [showSequenceWarning, setShowSequenceWarning] = useState(false)
  
  useEffect(() => {
    const assessmentSequenceStarted = sessionStorage.getItem('assessmentSequenceStarted') === 'true'
    const currentDriveAssessmentSequence = sessionStorage.getItem('currentDriveAssessmentSequence')
    
    if (!assessmentSequenceStarted || !currentDriveAssessmentSequence) {
      return
    }

    const handlePopState = (event: PopStateEvent) => {
      if (location.pathname.includes('/test/')) {
        event.preventDefault()
        setShowSequenceWarning(true)
        window.history.pushState(null, '', location.pathname)
      }
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = 'Navigation Back from test is not allowed, complete all Assessment in one go. Sure want to leave the test?'
      return event.returnValue
    }

    window.history.pushState(null, '', location.pathname)
    window.addEventListener('popstate', handlePopState)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [location.pathname])

  // Debug currentSession changes
  useEffect(() => {
    //console.log('currentSession changed:', currentSession)
  }, [currentSession])

  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard')
      return
    }

    // Security check: Ensure only candidates can access test pages
    if (user && user.role !== 'candidate') {
      toast.error('Access denied. Only candidates can access test pages.')
      navigate('/dashboard')
      return
    }

    // Load session info
    const loadSession = async () => {
      try {
        //console.log('Loading session for ID:', sessionId)
        const result = await dispatch(getSessionStatus(sessionId)).unwrap()
        if (['submitted', 'completed', 'expired', 'cancelled'].includes(result?.status)) {
          navigate(`/test/${sessionId}/completed`, { replace: true })
          return
        }
        //console.log('Session status result:', result)
        //console.log('Current session after load:', currentSession)
      } catch (error) {
        console.error('Failed to load session:', error)
        toast.error('Failed to load test session')
        navigate('/dashboard')
      }
    }
    
    loadSession()
  }, [sessionId, dispatch, navigate, user])

  const handleGuidelineCheck = (index: number) => {
    const newCheckedGuidelines = [...checkedGuidelines]
    newCheckedGuidelines[index] = !newCheckedGuidelines[index]
    setCheckedGuidelines(newCheckedGuidelines)
  }

  const handleProceedToSystemCheck = () => {
    if (checkedGuidelines.every(checked => checked)) {
      navigate(`/test/${sessionId}/system-check`)
    } else {
      toast.error('Please check all guidelines before proceeding')
    }
  }

  const allGuidelinesChecked = checkedGuidelines.every(checked => checked)

  // Get proper display name for assessment type
  const getAssessmentTypeDisplayName = (assessmentType: string) => {
    const typeMap: { [key: string]: string } = {
      'aptitude_reasoning': 'Aptitude & Reasoning',
      'aptitude': 'Aptitude & Reasoning',
      'fundamentals': 'Technical Knowledge',
      'cloud_fundamentals': 'Cloud Fundamentals',
      'presales_fundamentals': 'Pre-Sales Fundamentals',
      'english_communication': 'English Communication',
      'ai_fundamental': 'AI Fundamental',
      'coding': 'Coding Assessment',
      'case_study': 'Case Study',
      'case-study': 'Case Study',
      'aiml_assignment': 'AI/ML Assignment',
      'aiml': 'AI/ML Assignment',
      'quantitative_ability': 'Quantitative Ability',
      'logical_reasoning': 'Logical Reasoning',
      'verbal_ability': 'Verbal Ability'
    }
    return typeMap[assessmentType] || assessmentType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Assessment'
  }

  if (!currentSession) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading test session...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-6">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Test Details
          </h1>
          <p className="text-base text-gray-600">Please read the instructions carefully before proceeding</p>
        </div>

        <div className="card p-6 mb-6"><AutomaticTestInstructions configs={driveConfigs} showGuidelines={false} /></div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Test Instructions Card */}
          <div className="card p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <ExclamationTriangleIcon className="w-6 h-6 mr-2 text-warning-600" />
              Important Guidelines
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={checkedGuidelines[0]}
                  aria-label={guidelines[0]}
                  onClick={() => handleGuidelineCheck(0)}
                  className={`flex-shrink-0 w-6 h-6 rounded border-2 mr-3 mt-0.5 flex items-center justify-center transition-all duration-200 ${
                    checkedGuidelines[0]
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {checkedGuidelines[0] && <CheckIcon className="w-4 h-4" />}
                </button>
                <span className="text-sm text-gray-700 leading-relaxed">{guidelines[0]}</span>
              </li>
              <li className="flex items-start">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={checkedGuidelines[1]}
                  aria-label={guidelines[1]}
                  onClick={() => handleGuidelineCheck(1)}
                  className={`flex-shrink-0 w-6 h-6 rounded border-2 mr-3 mt-0.5 flex items-center justify-center transition-all duration-200 ${
                    checkedGuidelines[1]
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {checkedGuidelines[1] && <CheckIcon className="w-4 h-4" />}
                </button>
                <span className="text-sm text-gray-700 leading-relaxed">{guidelines[1]}</span>
              </li>
              <li className="flex items-start">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={checkedGuidelines[2]}
                  aria-label={guidelines[2]}
                  onClick={() => handleGuidelineCheck(2)}
                  className={`flex-shrink-0 w-6 h-6 rounded border-2 mr-3 mt-0.5 flex items-center justify-center transition-all duration-200 ${
                    checkedGuidelines[2]
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {checkedGuidelines[2] && <CheckIcon className="w-4 h-4" />}
                </button>
                <span className="text-sm text-gray-700 leading-relaxed">{guidelines[2]}</span>
              </li>
              <li className="flex items-start">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={checkedGuidelines[3]}
                  aria-label={guidelines[3]}
                  onClick={() => handleGuidelineCheck(3)}
                  className={`flex-shrink-0 w-6 h-6 rounded border-2 mr-3 mt-0.5 flex items-center justify-center transition-all duration-200 ${
                    checkedGuidelines[3]
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {checkedGuidelines[3] && <CheckIcon className="w-4 h-4" />}
                </button>
                <span className="text-sm text-gray-700 leading-relaxed">{guidelines[3]}</span>
              </li>
              <li className="flex items-start">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={checkedGuidelines[4]}
                  aria-label={guidelines[4]}
                  onClick={() => handleGuidelineCheck(4)}
                  className={`flex-shrink-0 w-6 h-6 rounded border-2 mr-3 mt-0.5 flex items-center justify-center transition-all duration-200 ${
                    checkedGuidelines[4]
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {checkedGuidelines[4] && <CheckIcon className="w-4 h-4" />}
                </button>
                <span className="text-sm text-gray-700 leading-relaxed">{guidelines[4]}</span>
              </li>
            </ul>
          </div>

          {/* Assessment Information Card */}
          <div className="card p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <ClockIcon className="w-6 h-6 mr-2 text-blue-600" />
              Assessment Information
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-700 font-medium">Duration:</span>
                <span className="text-base font-bold text-gray-900">
                  {isLoading ? 'Loading...' : 
                   error ? 'Error loading' :
                   currentSession?.duration_minutes ? `${currentSession.duration_minutes} minutes` : 
                   sectionConfig?.duration_minutes ? `${sectionConfig.duration_minutes} minutes` : 'Not available'}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-700 font-medium">Total Questions:</span>
                <span className="text-base font-bold text-gray-900">
                  {isLoading ? 'Loading...' : 
                   error ? 'Error loading' :
                    (currentSession?.total_questions || sectionConfig?.num_questions) ?? 'Not available'}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-700 font-medium">Question Type:</span>
                <span className="text-base font-bold text-gray-900">
                  {isLoading ? 'Loading...' : 
                   error ? 'Error loading' :
                   getAssessmentTypeDisplayName(currentSession?.assessment_type || '')}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-sm text-gray-700 font-medium">Proctoring:</span>
                <span className="text-base font-bold text-green-700">{currentSession.proctoring_enabled === false ? 'Disabled' : 'Enabled'}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <span className="text-sm text-gray-700 font-medium">Pause allowed:</span>
                <span className="text-base font-bold text-red-700">No</span>
              </div>
            </div>
          </div>
        </div>

        {/* Proceed Button */}
        <div className="flex justify-center">
          <button
            onClick={handleProceedToSystemCheck}
            disabled={!allGuidelinesChecked}
            className={`px-10 py-3 rounded-xl font-semibold text-base flex items-center space-x-2 shadow-lg transition-all duration-300 ${
              allGuidelinesChecked
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white hover:shadow-xl transform hover:scale-105 cursor-pointer'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <span>
              {allGuidelinesChecked 
                ? 'Proceed to System Check' 
                : 'Please check all guidelines first'}
            </span>
            <ArrowRightIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Assessment Sequence Navigation Warning Modal */}
      {showSequenceWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                <ExclamationTriangleIcon className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Navigation Warning</h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Navigation Back from test is not allowed, complete all Assessment in one go. Sure want to leave the test?
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={async () => {
                  try {
                    //console.log('[TestInstructionsPage] Leave Test clicked - submitting all assessments')
                    
                    const assessmentSequenceStr = sessionStorage.getItem('currentDriveAssessmentSequence')
                    const currentDriveId = sessionStorage.getItem('currentDriveId')
                    const candidateId = user?.id
                    
                    // Submit all assessments in the sequence (mark as completed)
                    if (assessmentSequenceStr && currentDriveId && candidateId) {
                      try {
                        const assessmentSequence = JSON.parse(assessmentSequenceStr)
                        const driveId = parseInt(currentDriveId, 10)
                        
                        // console.log('[TestInstructionsPage] Submitting all assessments:', {
                        //   driveId,
                        //   totalAssessments: assessmentSequence.length
                        // })
                        
                        // Submit all assessments in the sequence
                        for (const assessment of assessmentSequence) {
                          const assessmentId = assessment.id
                          
                          try {
                            // Mark assessment as submitted (with 0 responses since test wasn't started)
                            await dispatch(submitAssessment({
                              assessmentId,
                              candidateId,
                              driveId
                            }))
                            //console.log(`[TestInstructionsPage] Marked assessment ${assessmentId} as completed (left test)`)
                          } catch (error) {
                            console.error(`[TestInstructionsPage] Error submitting assessment ${assessmentId}:`, error)
                            // Continue with other assessments even if one fails
                          }
                        }
                      } catch (error) {
                        console.error('[TestInstructionsPage] Error processing assessments:', error)
                      }
                    }
                    
                    // Mark drive as left (so Start Test button is disabled)
                    if (currentDriveId) {
                      sessionStorage.setItem(`drive_${currentDriveId}_left`, 'true')
                      //console.log(`[TestInstructionsPage] Marked drive ${currentDriveId} as left`)
                    }
                    
                    // Clear assessment sequence data
                    sessionStorage.removeItem('currentDriveAssessmentSequence')
                    sessionStorage.removeItem('currentDriveAssessmentIndex')
                    sessionStorage.removeItem('currentDriveId')
                    sessionStorage.removeItem('assessmentSequenceStarted')
                    setShowSequenceWarning(false)
                    
                    toast.success('All assessments have been submitted. You have left the test.')
                    navigate('/dashboard')
                  } catch (error) {
                    console.error('[TestInstructionsPage] Error leaving test:', error)
                    toast.error('Error leaving test. Please try again.')
                    // Still clear sequence and navigate
                    const currentDriveId = sessionStorage.getItem('currentDriveId')
                    if (currentDriveId) {
                      sessionStorage.setItem(`drive_${currentDriveId}_left`, 'true')
                    }
                    sessionStorage.removeItem('currentDriveAssessmentSequence')
                    sessionStorage.removeItem('currentDriveAssessmentIndex')
                    sessionStorage.removeItem('currentDriveId')
                    sessionStorage.removeItem('assessmentSequenceStarted')
                    setShowSequenceWarning(false)
                    navigate('/dashboard')
                  }
                }}
                className="flex-1 px-4 py-2 border border-red-300 rounded-md text-red-700 bg-red-50 hover:bg-red-100 transition-colors font-medium"
              >
                Leave Test
              </button>
              <button
                onClick={() => {
                  setShowSequenceWarning(false)
                  window.history.pushState(null, '', location.pathname)
                }}
                className="flex-1 px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors font-medium"
              >
                Continue Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TestInstructionsPage
